import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { 
  Clock, CheckCircle, XCircle, Trophy, Zap, Star, 
  ArrowRight, Award, Flame, Target, Loader2, Hourglass, AlertCircle
} from 'lucide-react';
import { Progress } from '../components/ui/progress';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CompetitionPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const participantId = searchParams.get('participant_id') || sessionStorage.getItem('participant_id');
  
  const [currentRound, setCurrentRound] = useState(0);
  const [questions, setQuestions] = useState([]);
  // Tracks which round the `questions` array was loaded for. CRITICAL: we NEVER render the
  // questions UI unless questionsForRound === currentRound. This is the hard guarantee that
  // kills the "stale Round N questions visible after break_N → round_(N+1)" bug.
  const [questionsForRound, setQuestionsForRound] = useState(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [answerSubmitted, setAnswerSubmitted] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [score, setScore] = useState({ total: 0, streak: 0 });
  const [perfectRoundPossible, setPerfectRoundPossible] = useState(true);
  const [loading, setLoading] = useState(true);
  const [isFinished, setIsFinished] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  
  // Waiting state (two kinds):
  //  - inBreak: backend is in break_N → EVERYONE waits for next round, countdown is authoritative
  //  - localFinished: backend still in round_N but this participant pressed Finish Round — they see a "wait for round to end" panel with the round's remaining time
  const [inBreak, setInBreak] = useState(false);
  const [localFinished, setLocalFinished] = useState(false);
  const [graceCountdown, setGraceCountdown] = useState(0);
  const [graceLabel, setGraceLabel] = useState('');
  const [backendStatus, setBackendStatus] = useState('');
  const [roundSummary, setRoundSummary] = useState(null);
  // Confirmation modal for "Finish Round" — prevents accidental clicks during a live round.
  const [showFinishConfirm, setShowFinishConfirm] = useState(false);
  // Splash overlay shown for 1s right after a round transitions in — hides any race-condition flicker.
  const [roundSplash, setRoundSplash] = useState(0); // 0 means hidden; otherwise = round_num to show
  // When the round changes, lock Finish Round briefly so kids cannot accidentally tap it during the splash/transition.
  // Stored as seconds-left (counts down) — driven by a useEffect interval, avoids Date.now() in render.
  const [finishLockSecondsLeft, setFinishLockSecondsLeft] = useState(0);

  const questionStartTime = useRef(Date.now());
  const hasAutoFinished = useRef(false);
  const lastRoundSummaryFetched = useRef(0);
  // Refs mirroring state so the always-on polling effect can read latest values without re-subscribing.
  const currentRoundRef = useRef(0);
  const inBreakRef = useRef(false);
  const localFinishedRef = useRef(false);
  const loadInFlightForRound = useRef(0);  // monotonic guard for loadRoundQuestions
  const finishLockedRef = useRef(false);  // we have already POSTed /finish-round for current round
  const splashTimerRef = useRef(null);
  const newAnnIdRef = useRef(null);  // tracks last announcement id to detect new ones for the chime

  useEffect(() => { currentRoundRef.current = currentRound; }, [currentRound]);
  useEffect(() => { inBreakRef.current = inBreak; }, [inBreak]);
  useEffect(() => { localFinishedRef.current = localFinished; }, [localFinished]);

  // Detect new announcements (by id) → play a soft chime + briefly highlight the banner.
  const [announcementPulse, setAnnouncementPulse] = useState(false);
  useEffect(() => {
    if (!announcements || announcements.length === 0) return;
    const latest = announcements[announcements.length - 1];
    if (!latest || !latest.id) return;
    if (newAnnIdRef.current === latest.id) return;
    const isFirstLoad = newAnnIdRef.current === null;
    newAnnIdRef.current = latest.id;
    if (isFirstLoad) return;  // Don't chime on initial page load
    // Play a soft 2-tone WebAudio chime — non-blocking, fails silently on autoplay restrictions.
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (Ctx) {
        const ctx = new Ctx();
        const playTone = (freq, when, duration) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.frequency.value = freq;
          osc.type = 'sine';
          osc.connect(gain);
          gain.connect(ctx.destination);
          gain.gain.setValueAtTime(0.001, ctx.currentTime + when);
          gain.gain.exponentialRampToValueAtTime(0.2, ctx.currentTime + when + 0.02);
          gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + when + duration);
          osc.start(ctx.currentTime + when);
          osc.stop(ctx.currentTime + when + duration + 0.05);
        };
        playTone(880, 0, 0.18);
        playTone(1175, 0.18, 0.22);
        setTimeout(() => ctx.close && ctx.close(), 800);
      }
    } catch (e) { /* silent */ }
    setAnnouncementPulse(true);
    const t = setTimeout(() => setAnnouncementPulse(false), 5000);
    return () => clearTimeout(t);
  }, [announcements]);

  // Poll competition status — drives all state machine transitions.
  // Set up ONCE on mount. Uses refs to read latest state, so the interval is never
  // torn down mid-transition (kills the polling-race that caused "Round 1 Q6" flash).
  useEffect(() => {
    if (!participantId) return;
    let cancelled = false;
    const pollStatus = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/competition/status`);
        if (cancelled) return;
        const data = response.data;
        setIsPaused(data.is_paused || false);
        setBackendStatus(data.status);
        setGraceLabel(data.grace_label || '');
        if (data.announcements) {
          setAnnouncements(data.announcements);
        }

        const status = data.status;
        const remaining = data.time_remaining_seconds || 0;
        const cr = currentRoundRef.current;
        const wasInBreak = inBreakRef.current;

        // Restore the per-participant finish-round lock from backend (survives refresh).
        const myLocks = (data.participant_round_locks || {})[participantId] || [];
        if (status.startsWith('round_')) {
          const rn = parseInt(status.split('_')[1]);
          const isLocked = myLocks.includes(rn);
          // Only restore localFinished from server when we don't already have it AND we are in the matching round.
          if (isLocked && cr === rn && !localFinishedRef.current) {
            setLocalFinished(true);
            finishLockedRef.current = true;
          }
        }

        if (status === 'finished') {
          setIsFinished(true);
          setLoading(false);
          setInBreak(false);
          setLocalFinished(false);
          setQuestions([]);
          setQuestionsForRound(0);
          return;
        }

        if (status === 'grace_r1' || status.startsWith('grace_reset_')) {
          // Pre-round grace period
          setInBreak(false);
          setLocalFinished(false);
          finishLockedRef.current = false;
          setGraceCountdown(remaining);
          if (status.startsWith('grace_reset_')) {
            const resetRound = parseInt(status.split('_').pop());
            if (resetRound !== cr) {
              setCurrentRound(0);
              setQuestions([]);
              setCurrentQuestionIndex(0);
              setSelectedAnswer(null);
              setAnswerSubmitted(false);
              setLastResult(null);
              hasAutoFinished.current = false;
            }
          }
          setLoading(false);
          return;
        }

        if (status.startsWith('break_')) {
          const afterRound = parseInt(status.split('_')[1]);
          if (!wasInBreak || cr !== afterRound) {
            setCurrentRound(afterRound);
            setInBreak(true);
            setLocalFinished(false);
            finishLockedRef.current = false;
            // Pre-emptively clear questions array so the moment break_N ends and round_(N+1)
            // starts, no stale round-N question can flash before the new ones load.
            setQuestions([]);
            setQuestionsForRound(0);
            setCurrentQuestionIndex(0);
            // Fetch round summary once per break
            if (lastRoundSummaryFetched.current !== afterRound) {
              lastRoundSummaryFetched.current = afterRound;
              try {
                const summaryRes = await axios.get(`${API_URL}/api/competition/round-summary/${afterRound}/${participantId}`);
                if (!cancelled) {
                  setRoundSummary(summaryRes.data);
                  setScore({ total: summaryRes.data.cumulative_total || 0, streak: 0 });
                }
              } catch {
                if (!cancelled) setRoundSummary(null);
              }
            }
          }
          setGraceCountdown(remaining);
          return;
        }

        if (status.startsWith('round_')) {
          const roundNum = parseInt(status.split('_')[1]);
          // Monotonic guard: only advance to a NEW round number. Never regress mid-event.
          if (roundNum !== cr) {
            if (roundNum < cr) {
              // Backend somehow regressed — ignore (defensive).
              return;
            }
            // ATOMIC ROUND TRANSITION: clear ALL per-round state in one batch BEFORE loading.
            // This kills the "stale Round 1 questions visible while Round 2 is loading" flash.
            console.warn('[round-engine] transition →', cr, '→', roundNum, 'status=', status);
            setQuestions([]);
            setQuestionsForRound(0);  // No questions for ANY round right now → forces "Preparing…" screen
            setCurrentQuestionIndex(0);
            setSelectedAnswer(null);
            setAnswerSubmitted(false);
            setLastResult(null);
            setRoundSummary(null);
            setLoading(true);  // SYNCHRONOUSLY mark loading BEFORE the async fetch
            setCurrentRound(roundNum);
            setInBreak(false);
            setLocalFinished(false);
            finishLockedRef.current = false;
            hasAutoFinished.current = false;
            lastRoundSummaryFetched.current = 0;
            // Show 1s "Round N starting" splash to hide any micro-flicker during load.
            setRoundSplash(roundNum);
            if (splashTimerRef.current) clearTimeout(splashTimerRef.current);
            splashTimerRef.current = setTimeout(() => setRoundSplash(0), 1200);
            // Lock the Finish Round button for the first 10 seconds of the new round.
            setFinishLockSecondsLeft(10);
            loadRoundQuestionsSafely(roundNum);
          }
          setTimeRemaining(remaining);
        }
      } catch (err) {
        if (!cancelled) console.error('Failed to poll status:', err);
      }
    };

    pollStatus();
    const interval = setInterval(pollStatus, 2000);
    return () => {
      cancelled = true;
      clearInterval(interval);
      if (splashTimerRef.current) clearTimeout(splashTimerRef.current);
    };
  }, [participantId]);

  // Tick the Finish Round lockout down to 0 by 1s steps.
  useEffect(() => {
    if (finishLockSecondsLeft <= 0) return;
    const t = setInterval(() => setFinishLockSecondsLeft((n) => Math.max(0, n - 1)), 1000);
    return () => clearInterval(t);
  }, [finishLockSecondsLeft]);

  // No local timer needed — timeRemaining is driven by backend polling (authoritative clock).
  // When the backend round timer hits 0 we DO NOT flip to localFinished — the next /status poll
  // will transition us to break_N cleanly. The old "auto-flip on timeRemaining===0" useEffect was
  // a race source: it could fire with stale questions while a new round was loading.

  const loadRoundQuestionsSafely = async (roundNum) => {
    // Monotonic guard — if a newer round-load has been scheduled while we were in flight, drop our result.
    loadInFlightForRound.current = roundNum;
    setLoading(true);
    try {
      // Cache-bust the URL so no CDN/HTTP cache can ever serve us Round (N-1)'s response.
      const url = `${API_URL}/api/competition/round/${roundNum}/questions?participant_id=${participantId}&_t=${Date.now()}`;
      const response = await axios.get(url);
      // Drop response if a newer round started while we were awaiting.
      if (loadInFlightForRound.current !== roundNum) {
        console.warn('[round-engine] dropped stale load for round', roundNum, '(latest=', loadInFlightForRound.current, ')');
        return;
      }
      if (currentRoundRef.current !== roundNum) {
        console.warn('[round-engine] dropped load — currentRound moved to', currentRoundRef.current, 'while loading', roundNum);
        return;
      }
      const qs = response.data.questions || [];
      // Sanity-check the response — every question MUST have round_number === roundNum.
      const wrong = qs.find((q) => q.round_number && q.round_number !== roundNum);
      if (wrong) {
        console.error('[round-engine] backend returned wrong round! expected', roundNum, 'got', wrong.round_number, '— refusing to render');
        return;
      }
      setQuestions(qs);
      setQuestionsForRound(roundNum);  // mark THIS round's questions as live-rendered
      setCurrentQuestionIndex(0);
      setSelectedAnswer(null);
      setAnswerSubmitted(false);
      setPerfectRoundPossible(true);
      hasAutoFinished.current = false;
      questionStartTime.current = Date.now();
    } catch (err) {
      console.error('[round-engine] Failed to load questions for round', roundNum, err);
    } finally {
      if (loadInFlightForRound.current === roundNum) {
        setLoading(false);
      }
    }
  };

  // Backwards-compat alias removed — loadRoundQuestionsSafely is the only callsite.

  const handleAnswerSelect = (option) => {
    if (answerSubmitted || localFinished) return;
    setSelectedAnswer(option);
    // Round 8 (Riddle Sprint) is auto-submit: tapping an answer fires submitAnswer
    // immediately. We must pass the freshly chosen option because setSelectedAnswer
    // is async and submitAnswer reads from state.
    if (currentRound === 8) {
      submitAnswer(option);
    }
  };

  const submitAnswer = async (forcedOption = null) => {
    const optionToSend = forcedOption || selectedAnswer;
    if (!optionToSend || answerSubmitted) return;

    const responseTime = (Date.now() - questionStartTime.current) / 1000;

    try {
      const response = await axios.post(`${API_URL}/api/competition/submit-answer`, {
        participant_id: participantId,
        question_id: questions[currentQuestionIndex].id,
        selected_option: optionToSend,
        response_time_seconds: responseTime
      });

      setLastResult(response.data);
      setAnswerSubmitted(true);
      setScore({
        total: response.data.total_points,
        streak: response.data.current_streak
      });
      setPerfectRoundPossible(response.data.perfect_round_possible);

      // For Round 8, auto-advance. On the LAST question (no next exists) auto-finish the round.
      if (currentRound === 8) {
        const isLast = currentQuestionIndex >= questions.length - 1;
        setTimeout(() => {
          if (isLast) {
            finishRound();
          } else {
            nextQuestion();
          }
        }, 1500);
      }
    } catch (err) {
      console.error('Failed to submit answer:', err);
    }
  };

  const nextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
      setSelectedAnswer(null);
      setAnswerSubmitted(false);
      setLastResult(null);
      questionStartTime.current = Date.now();
    }
  };

  const finishRound = async () => {
    // User chose to finish early (or answered all questions) — lock this participant's answers
    // for this round. We optimistically flip localFinished, then POST to backend so the lock
    // survives a page refresh. Idempotent.
    if (localFinished) return;
    setLocalFinished(true);
    if (finishLockedRef.current) return;
    finishLockedRef.current = true;
    try {
      await axios.post(`${API_URL}/api/competition/finish-round`, {
        participant_id: participantId,
        round_num: currentRound,
      });
    } catch (err) {
      // If the POST fails, we keep localFinished=true so the user sees the waiting panel.
      // A refresh would lose the lock — acceptable degradation; logs surface the failure.
      console.error('Failed to persist finish-round lock:', err);
    }
  };

  // ============ RENDER STATES ============

  // Shared banner shown in EVERY render state when an announcement is live.
  // Pulsing border + bell icon when a NEW one arrives; click ✕ to mute the visual pulse.
  const latestAnnouncement = announcements && announcements.length > 0 ? announcements[announcements.length - 1] : null;
  const AnnouncementBanner = latestAnnouncement ? (
    <div
      className={`fixed top-3 left-1/2 -translate-x-1/2 z-40 max-w-2xl w-[92%] bg-[#fef3c7] border-2 ${announcementPulse ? 'border-[#ef4444] animate-pulse shadow-[0_0_24px_rgba(239,68,68,0.45)]' : 'border-[#f59e0b]'} rounded-2xl px-4 py-2 flex items-center gap-3`}
      data-testid="participant-announcement-banner"
    >
      <span className={`text-2xl flex-shrink-0 ${announcementPulse ? 'animate-bounce' : ''}`} aria-hidden="true">&#128227;</span>
      <p className="text-[#92400e] font-semibold text-sm flex-1 leading-snug">{latestAnnouncement.message}</p>
      <button
        onClick={() => setAnnouncementPulse(false)}
        className="text-[#92400e] hover:text-[#7c2d12] text-lg leading-none px-2"
        aria-label="Mute"
        data-testid="participant-announcement-mute"
      >&times;</button>
    </div>
  ) : null;

  // Paused Screen
  if (isPaused && !isFinished) {
    return (
      <div className="min-h-screen bg-[#fdfdfc] flex items-center justify-center p-6">
        {AnnouncementBanner}
        <div className="text-center max-w-md animate-fade-in-up">
          <div className="w-24 h-24 bg-[#f59e0b] rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-[0_6px_0_#d97706]">
            <Clock className="w-12 h-12 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-[#0f172a] mb-4" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            Competition Paused
          </h1>
          <p className="text-[#64748b] mb-6">The host has paused the competition. Please wait...</p>
          {announcements.length > 0 && (
            <div className="space-y-2">
              {announcements.map((a) => (
                <div key={a.id} className="bg-[#fef3c7] rounded-2xl px-4 py-3 border-2 border-[#f59e0b] text-left">
                  <p className="font-semibold text-[#92400e] text-sm">{a.message}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Finished Screen
  if (isFinished) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#2565eb] to-[#1e3a8a] flex items-center justify-center p-6">
        {AnnouncementBanner}
        <div className="text-center max-w-md animate-fade-in-up">
          <div className="w-32 h-32 bg-[#face39] rounded-full flex items-center justify-center mx-auto mb-8 shadow-[0_8px_0_#d97706]">
            <Trophy className="w-16 h-16 text-[#0f172a]" />
          </div>
          <h1 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            Competition Complete!
          </h1>
          <p className="text-white/80 text-lg mb-8">Congratulations on completing the Brain Gym Olympiad!</p>
          <div className="card-olympiad p-6 mb-8">
            <p className="text-sm text-[#64748b] mb-2">Your Final Score</p>
            <p className="text-4xl font-bold text-[#0f172a]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              {score.total} points
            </p>
          </div>
          <button onClick={() => navigate('/results')} className="btn-primary flex items-center justify-center gap-2 mx-auto" data-testid="view-results-button">
            View Results & Leaderboard <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    );
  }

  // ============ GRACE PERIOD SCREEN (pre-R1, or after reset-round) ============
  if (backendStatus === 'grace_r1' || backendStatus.startsWith('grace_reset_')) {
    const gm = Math.floor(graceCountdown / 60);
    const gs = graceCountdown % 60;
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#2565eb] to-[#1e3a8a] flex items-center justify-center p-6">
        {AnnouncementBanner}
        <div className="text-center animate-fade-in-up">
          <div className="w-24 h-24 bg-white/15 backdrop-blur rounded-3xl flex items-center justify-center mx-auto mb-8">
            <Hourglass className="w-12 h-12 text-white animate-pulse" />
          </div>
          <p className="text-white/80 text-xl mb-4">{graceLabel || 'Round starting in'}</p>
          <p className="text-8xl font-bold text-white font-mono mb-4" style={{ fontFamily: 'Fredoka, sans-serif' }} data-testid="grace-countdown">
            {gm}:{gs.toString().padStart(2, '0')}
          </p>
          <p className="text-white/70 text-sm">Get ready — questions will load automatically</p>
        </div>
      </div>
    );
  }

  // ============ WAITING ROOM (between rounds — global break driven by backend) ============
  if (inBreak) {
    const isLastRound = currentRound >= 8;
    const waitMin = Math.floor(graceCountdown / 60);
    const waitSec = graceCountdown % 60;
    
    return (
      <div className="min-h-screen bg-[#fdfdfc] flex flex-col">
        {AnnouncementBanner}
        <header className="p-6 bg-gradient-to-r from-[#2565eb] to-[#1e40af]">
          <div className="max-w-3xl mx-auto text-center text-white">
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              Round {currentRound} Complete!
            </h1>
          </div>
        </header>

        <main className="flex-1 p-6 max-w-3xl mx-auto w-full">
          {/* Round Summary */}
          {roundSummary && (
            <div className="card-olympiad p-6 sm:p-8 mb-6 animate-fade-in-up">
              <h2 className="text-xl font-bold text-[#0f172a] mb-6" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                Round {currentRound} Score
              </h2>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-[#e2e8f0]">
                  <span className="text-[#64748b]">Correct Answers</span>
                  <span className="font-bold text-[#0f172a]">
                    {roundSummary.round_score?.correct_answers || 0} / {roundSummary.round_score?.questions_answered || questions.length}
                  </span>
                </div>
                {(roundSummary.round_score?.speed_bonus || 0) > 0 && (
                  <div className="flex justify-between items-center py-2 border-b border-[#e2e8f0]">
                    <span className="text-[#64748b] flex items-center gap-2"><Zap className="w-4 h-4 text-[#f59e0b]" /> Speed Bonus</span>
                    <span className="font-bold text-[#f59e0b]">+{roundSummary.round_score.speed_bonus} pts</span>
                  </div>
                )}
                {(roundSummary.round_score?.streak_bonus || 0) > 0 && (
                  <div className="flex justify-between items-center py-2 border-b border-[#e2e8f0]">
                    <span className="text-[#64748b] flex items-center gap-2"><Flame className="w-4 h-4 text-[#ef4444]" /> Streak Bonus</span>
                    <span className="font-bold text-[#ef4444]">+{roundSummary.round_score.streak_bonus} pts</span>
                  </div>
                )}
                <div className="flex justify-between items-center pt-3">
                  <span className="font-bold text-[#0f172a]">Round Total</span>
                  <span className="text-2xl font-bold text-[#2565eb]">{roundSummary.round_score?.total_points || 0} pts</span>
                </div>
              </div>
            </div>
          )}

          {/* Rank & Cumulative */}
          {roundSummary && (
            <div className="grid grid-cols-2 gap-4 mb-6 animate-fade-in-up">
              <div className="card-olympiad p-5 text-center">
                <p className="text-sm text-[#64748b] mb-1">Total Score</p>
                <p className="text-3xl font-bold text-[#0f172a]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                  {roundSummary.cumulative_total || 0}
                </p>
              </div>
              <div className="card-olympiad p-5 text-center">
                <p className="text-sm text-[#64748b] mb-1">Current Rank</p>
                <p className="text-3xl font-bold text-[#2565eb]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                  #{roundSummary.rank || '-'}
                </p>
              </div>
            </div>
          )}

          {/* Waiting Room Countdown (authoritative from backend) */}
          {!isLastRound ? (
            <div className="card-olympiad p-8 text-center animate-fade-in-up border-2 border-[#2565eb]" data-testid="waiting-room">
              <Hourglass className="w-10 h-10 text-[#2565eb] mx-auto mb-4 animate-pulse" />
              <h3 className="text-lg font-bold text-[#0f172a] mb-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                Waiting Room
              </h3>
              <p className="text-[#64748b] mb-4">Next round starts in</p>
              <div className="text-5xl font-bold font-mono text-[#2565eb] mb-2" data-testid="waiting-countdown">
                {waitMin}:{waitSec.toString().padStart(2, '0')}
              </div>
              <p className="text-sm text-[#64748b]">Get ready for Round {currentRound + 1}!</p>
            </div>
          ) : (
            <div className="card-olympiad p-8 text-center animate-fade-in-up border-2 border-[#22c55e]">
              <Trophy className="w-10 h-10 text-[#22c55e] mx-auto mb-4" />
              <h3 className="text-lg font-bold text-[#0f172a] mb-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                All Rounds Complete!
              </h3>
              <p className="text-[#64748b]">Waiting for final results...</p>
            </div>
          )}
        </main>
      </div>
    );
  }

  // ============ LOCAL FINISHED (participant done but round still running for others) ============
  if (localFinished && backendStatus.startsWith('round_')) {
    const rm = Math.floor(timeRemaining / 60);
    const rs = timeRemaining % 60;
    return (
      <div className="min-h-screen bg-[#fdfdfc] flex items-center justify-center p-6">
        {AnnouncementBanner}
        <div className="max-w-md w-full text-center animate-fade-in-up">
          <div className="w-20 h-20 bg-[#eff6ff] rounded-3xl flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-[#2565eb]" />
          </div>
          <h1 className="text-2xl font-bold text-[#0f172a] mb-3" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            You&apos;ve finished Round {currentRound}!
          </h1>
          <p className="text-[#64748b] mb-8">Other participants are still answering. The round ends automatically.</p>
          <div className="card-olympiad p-6">
            <p className="text-sm text-[#64748b] mb-2">Round ends in</p>
            <p className="text-4xl font-bold font-mono text-[#0f172a]" data-testid="local-waiting-countdown">
              {rm}:{rs.toString().padStart(2, '0')}
            </p>
          </div>
          <p className="text-xs text-[#94a3b8] mt-4">Break room opens next — the leaderboard will update there.</p>
        </div>
      </div>
    );
  }

  // Loading — show loading screen if loading, or if questions array is empty, OR if
  // questions don't belong to the current round (HARD GUARANTEE — kills stale-render).
  if (loading || questions.length === 0 || questionsForRound !== currentRound) {
    return (
      <div className="min-h-screen bg-[#fdfdfc] flex items-center justify-center">
        {AnnouncementBanner}
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-[#2565eb] animate-spin mx-auto mb-4" />
          <p className="text-[#64748b]">{currentRound > 0 ? `Preparing Round ${currentRound}…` : 'Loading…'}</p>
        </div>
      </div>
    );
  }

  // ============ ACTIVE ROUND ============
  const currentQuestion = questions[currentQuestionIndex];
  // Belt-and-suspenders: also gate on the question's own round_number field.
  if (!currentQuestion || (currentQuestion.round_number && currentQuestion.round_number !== currentRound)) {
    return (
      <div className="min-h-screen bg-[#fdfdfc] flex items-center justify-center">
        {AnnouncementBanner}
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-[#2565eb] animate-spin mx-auto mb-4" />
          <p className="text-[#64748b]">Preparing Round {currentRound}…</p>
        </div>
      </div>
    );
  }
  const progress = ((currentQuestionIndex + 1) / questions.length) * 100;
  const isRound8 = currentRound === 8;
  const minutes = Math.floor(timeRemaining / 60);
  const seconds = timeRemaining % 60;
  const isLastQuestion = currentQuestionIndex >= questions.length - 1;
  // Finish Round lockout state — finishLockSecondsLeft is updated by interval (no Date.now in render).
  const finishLocked = finishLockSecondsLeft > 0;
  const finishBtnClass = `px-5 py-2.5 rounded-xl font-bold border-2 text-sm transition-all ${finishLocked ? 'border-[#e2e8f0] text-[#cbd5e1] cursor-not-allowed' : 'border-[#e2e8f0] text-[#64748b] hover:border-[#d97706] hover:text-[#d97706]'}`;
  const finishBtnLabel = finishLocked ? `Finish Round (${finishLockSecondsLeft}s)` : 'Finish Round';

  return (
    <div className="min-h-screen bg-[#fdfdfc] flex flex-col">
      {AnnouncementBanner}
      {/* Header */}
      <header className={`p-4 ${isRound8 ? 'bg-gradient-to-r from-[#dc2626] to-[#991b1b]' : 'bg-gradient-to-r from-[#2565eb] to-[#1e40af]'}`}>
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="text-white">
            <p className="text-sm font-medium opacity-80">Round {currentRound}</p>
            <p className="font-bold">{currentQuestion.round_category || ''}</p>
          </div>
          
          <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${timeRemaining < 60 ? 'bg-[#ef4444] animate-pulse' : 'bg-white/20'}`}>
            <Clock className="w-5 h-5 text-white" />
            <span className="text-xl font-bold text-white font-mono">
              {minutes}:{seconds.toString().padStart(2, '0')}
            </span>
          </div>
          
          <div className="text-right text-white">
            <p className="text-sm opacity-80">Score</p>
            <p className="font-bold">{score.total} pts</p>
          </div>
        </div>
      </header>

      {/* Progress & Streak */}
      <div className="px-4 py-2 bg-white border-b border-[#e2e8f0]">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-[#64748b]">Question {currentQuestionIndex + 1} of {questions.length}</span>
            {score.streak >= 3 && (
              <span className="flex items-center gap-1 text-[#ef4444] font-semibold">
                <Flame className="w-4 h-4" /> {score.streak} streak!
              </span>
            )}
            {perfectRoundPossible && currentQuestionIndex > 0 && (
              <span className="flex items-center gap-1 text-[#a855f7] font-semibold animate-pulse">
                <Star className="w-4 h-4" /> Perfect round!
              </span>
            )}
          </div>
          <Progress value={progress} className="h-2" />
        </div>
      </div>

      {/* Question */}
      <main className="flex-1 p-4 sm:p-6 max-w-3xl mx-auto w-full">
        <div className="card-olympiad p-6 sm:p-8 animate-fade-in-up" key={currentQuestionIndex}>
          <h2 className="text-xl sm:text-2xl font-bold text-[#0f172a] mb-8" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            {currentQuestion.question_text}
          </h2>

          <div className="space-y-3">
            {['A', 'B', 'C', 'D'].map((option) => {
              const optionKey = `option_${option.toLowerCase()}`;
              const optionText = currentQuestion[optionKey];
              const isSelected = selectedAnswer === option;
              const isCorrect = lastResult?.correct_option === option;
              const wasSelected = lastResult?.is_correct === false && isSelected;
              
              let buttonStyle = "w-full text-left rounded-2xl p-4 sm:p-5 font-semibold transition-all border-2";
              
              if (answerSubmitted) {
                if (isCorrect) buttonStyle += " bg-[#f0fdf4] border-[#22c55e] text-[#15803d] shadow-[0_4px_0_#22c55e]";
                else if (wasSelected) buttonStyle += " bg-[#fef2f2] border-[#ef4444] text-[#b91c1c] shadow-[0_4px_0_#ef4444]";
                else buttonStyle += " bg-white border-[#e2e8f0] text-[#64748b]";
              } else if (isSelected) {
                buttonStyle += " bg-[#eff6ff] border-[#2565eb] text-[#2565eb] shadow-[0_4px_0_#2565eb]";
              } else {
                buttonStyle += " bg-white border-[#e2e8f0] text-[#0f172a] hover:border-[#2565eb] hover:bg-[#eff6ff] cursor-pointer";
              }
              
              return (
                <button key={option} onClick={() => handleAnswerSelect(option)} disabled={answerSubmitted} className={buttonStyle} data-testid={`comp-option-${option.toLowerCase()}`}>
                  <span className="flex items-center gap-4">
                    <span className="w-10 h-10 rounded-xl bg-[#f1f5f9] flex items-center justify-center font-bold text-[#64748b]">{option}</span>
                    <span className="flex-1">{optionText}</span>
                    {answerSubmitted && isCorrect && <CheckCircle className="w-6 h-6 text-[#22c55e]" />}
                    {answerSubmitted && wasSelected && <XCircle className="w-6 h-6 text-[#ef4444]" />}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Action Buttons */}
        {!isRound8 && (
          <div className="flex justify-between mt-6">
            {/* Finish Round Early button — locked for the first 10s of each round to prevent accidental taps during the splash. */}
            <button
              onClick={() => !finishLocked && setShowFinishConfirm(true)}
              disabled={finishLocked}
              className={finishBtnClass}
              data-testid="finish-round-btn"
            >
              {finishBtnLabel}
            </button>

            {!answerSubmitted ? (
              <button onClick={() => submitAnswer()} disabled={!selectedAnswer} className="btn-primary flex items-center gap-2 disabled:opacity-50" data-testid="submit-comp-answer-button">
                <CheckCircle className="w-5 h-5" /> Submit Answer
              </button>
            ) : (
              <button
                onClick={isLastQuestion ? () => setShowFinishConfirm(true) : nextQuestion}
                className="btn-secondary flex items-center gap-2"
                data-testid="next-comp-question-button"
              >
                {isLastQuestion ? 'Finish Round' : 'Next Question'} <ArrowRight className="w-5 h-5" />
              </button>
            )}
          </div>
        )}

        {/* Round 8: auto-advance, but show finish button */}
        {isRound8 && (
          <div className="flex justify-start mt-6">
            <button
              onClick={() => !finishLocked && setShowFinishConfirm(true)}
              disabled={finishLocked}
              className={finishBtnClass}
              data-testid="finish-round-btn"
            >
              {finishBtnLabel}
            </button>
          </div>
        )}

        {/* Result Feedback */}
        {answerSubmitted && lastResult && (
          <div className={`mt-6 p-4 rounded-2xl border-2 animate-fade-in-up ${lastResult.is_correct ? 'bg-[#f0fdf4] border-[#22c55e]' : 'bg-[#fef2f2] border-[#ef4444]'}`}>
            <div className="flex items-center justify-between">
              <p className={`font-semibold flex items-center gap-2 ${lastResult.is_correct ? 'text-[#16a34a]' : 'text-[#dc2626]'}`}>
                {lastResult.is_correct ? <><CheckCircle className="w-5 h-5" /> Correct!</> : <><XCircle className="w-5 h-5" /> Incorrect</>}
              </p>
              <p className="font-bold text-[#0f172a]">+{lastResult.points_earned} pts</p>
            </div>
            {lastResult.breakdown && (lastResult.breakdown.speed_bonus > 0 || lastResult.breakdown.streak_bonus > 0) && (
              <div className="flex gap-4 mt-2 text-sm">
                {lastResult.breakdown.speed_bonus > 0 && <span className="flex items-center gap-1 text-[#f59e0b]"><Zap className="w-4 h-4" /> +{lastResult.breakdown.speed_bonus} speed</span>}
                {lastResult.breakdown.streak_bonus > 0 && <span className="flex items-center gap-1 text-[#ef4444]"><Flame className="w-4 h-4" /> +{lastResult.breakdown.streak_bonus} streak</span>}
              </div>
            )}
          </div>
        )}
      </main>

      {/* Finish-Round Confirmation Modal — guards against accidental clicks */}
      {showFinishConfirm && (
        <div
          className="fixed inset-0 bg-black/55 z-50 flex items-center justify-center p-4"
          onClick={() => setShowFinishConfirm(false)}
          data-testid="finish-confirm-modal"
        >
          <div
            className="bg-white rounded-3xl p-6 max-w-md w-full shadow-2xl border-2 border-[#f59e0b]"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-base font-bold text-[#0f172a] mb-2 flex items-center gap-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              <AlertCircle className="w-5 h-5 text-[#f59e0b]" /> Finish this round?
            </h3>
            <p className="text-sm font-medium text-[#475569] mb-5 leading-relaxed">
              Are you sure? Any unanswered questions will not score points. You will not be able to come back to this round.
            </p>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setShowFinishConfirm(false)}
                className="px-4 py-2 rounded-xl text-sm font-bold text-[#64748b] hover:bg-[#f1f5f9] transition-all"
                data-testid="finish-cancel-btn"
              >
                Keep Playing
              </button>
              <button
                onClick={() => { setShowFinishConfirm(false); finishRound(); }}
                className="px-4 py-2 rounded-xl text-sm font-bold bg-[#d97706] text-white hover:bg-[#b45309] transition-all flex items-center gap-2"
                data-testid="finish-confirm-btn"
              >
                <CheckCircle className="w-4 h-4" /> Yes, Finish
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Round Splash Overlay — shows for ~1s when a new round transitions in, hides any micro-flicker. */}
      {roundSplash > 0 && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(37,101,235,0.97) 0%, rgba(30,58,138,0.99) 100%)' }}
          data-testid="round-splash-overlay"
        >
          <div className="text-center animate-fade-in-up">
            <p className="text-white/80 text-xl mb-2 tracking-wide">Get ready</p>
            <p className="text-7xl font-bold text-white" style={{ fontFamily: 'Fredoka, sans-serif' }} data-testid="round-splash-label">
              Round {roundSplash}
            </p>
            <p className="text-white/70 text-sm mt-3">Starting now…</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompetitionPage;
